/*!
 * ASKPEPLY — section runtime
 * ---------------------------------------------------------------------------
 * Vanilla JS. Optional deps (loaded deferred by the section, before this file):
 *   THREE (r128)            → particle globe (skipped gracefully if missing)
 *   gsap + ScrollTrigger    → scroll reveals + telemetry counters
 *                             (IntersectionObserver + rAF fallback if missing)
 *
 * Every instance is bound to one [data-ap-root] element and is fully
 * disposable, so the Shopify theme editor can re-render the section
 * (shopify:section:load / unload) without leaking WebGL contexts, timers,
 * listeners or ScrollTriggers.
 */
(function () {
  'use strict';

  if (window.Askpeply) {
    // Script re-executed (e.g. by the theme editor): just boot new roots.
    window.Askpeply.boot(document);
    return;
  }

  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var instances = [];

  /* ------------------------------------------------------------------ utils */
  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function easeOutQuad(t) { return 1 - (1 - t) * (1 - t); }
  function escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function hexToRgb01(hex, fallback) {
    var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || '').trim());
    if (!m) return fallback;
    return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
  }
  /* Minimal, safe markdown → HTML for AI replies (escape first, then format). */
  function renderMarkdown(src) {
    var lines = escapeHtml(String(src || '').replace(/\r/g, '')).split('\n');
    var html = '', list = null, para = [];
    function inline(t) {
      return t
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
        .replace(/\[([^\]]+)\]\(((?:https?:\/\/|#|\/|mailto:)[^\s)]+)\)/g, function (m, label, url) {
          var ext = /^https?:/i.test(url);
          return '<a href="' + url + '"' + (ext ? ' target="_blank" rel="noopener nofollow"' : '') + '>' + label + '</a>';
        });
    }
    function flushPara() { if (para.length) { html += '<p>' + inline(para.join(' ')) + '</p>'; para = []; } }
    function flushList() { if (list) { html += '</' + list + '>'; list = null; } }
    lines.forEach(function (raw) {
      var line = raw.trim();
      var ul = /^[-*•]\s+(.*)$/.exec(line), ol = /^\d+[.)]\s+(.*)$/.exec(line), hd = /^#{1,6}\s+(.*)$/.exec(line);
      if (!line) { flushPara(); flushList(); return; }
      if (ul || ol) {
        flushPara();
        var type = ul ? 'ul' : 'ol';
        if (list !== type) { flushList(); html += '<' + type + '>'; list = type; }
        html += '<li>' + inline((ul || ol)[1]) + '</li>';
        return;
      }
      flushList();
      if (hd) { flushPara(); html += '<p><strong>' + inline(hd[1]) + '</strong></p>'; return; }
      para.push(line);
    });
    flushPara(); flushList();
    return html;
  }
  function hasGsap() { return typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined'; }

  /* ================================================================== Instance */
  function Instance(root) {
    this.root = root;
    this.alive = true;
    this._timeouts = [];
    this._intervals = [];
    this._listeners = [];
    this._triggers = [];
    this._observers = [];
    this._tweenTargets = [];
    this._raf = 0;
    this.designMode = !!(window.Shopify && window.Shopify.designMode);
  }

  Instance.prototype.later = function (fn, ms) {
    var self = this;
    var id = setTimeout(function () { if (self.alive) fn(); }, ms);
    this._timeouts.push(id);
    return id;
  };
  Instance.prototype.every = function (fn, ms) {
    var id = setInterval(fn, ms);
    this._intervals.push(id);
    return id;
  };
  Instance.prototype.on = function (target, type, fn, opts) {
    if (!target) return;
    target.addEventListener(type, fn, opts || false);
    this._listeners.push([target, type, fn, opts || false]);
  };

  Instance.prototype.init = function () {
    var root = this.root;
    this.initCountdown();
    this.initMenu();
    this.initAnchors();
    this.initWordCycle();
    this.initReveals();
    this.initCounters();
    this.initFaq();
    this.initStory();
    this.initIntake();
    this.initChat();
    this.initGlobe();
    root.classList.add('ap-booted');
    document.documentElement.classList.add('ap-booted');
  };

  Instance.prototype.destroy = function () {
    this.alive = false;
    this._timeouts.forEach(clearTimeout);
    this._intervals.forEach(clearInterval);
    this._listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    this._observers.forEach(function (o) { o.disconnect(); });
    this._triggers.forEach(function (t) { t.kill(); });
    if (window.gsap && this._tweenTargets.length) window.gsap.killTweensOf(this._tweenTargets);
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._abortChat) this._abortChat();
    if (this.globe) this.globe.dispose();
    this.root.classList.remove('ap-booted');
    this._timeouts = this._intervals = this._listeners = this._observers = this._triggers = this._tweenTargets = [];
  };

  /* ------------------------------------------------------------------ countdown */
  Instance.prototype.initCountdown = function () {
    var el = $(this.root, '[data-ap-countdown]');
    if (!el) return;
    var target = new Date(el.getAttribute('data-target'));
    if (isNaN(target.getTime())) return;
    var expired = el.getAttribute('data-expired') || '';
    function tick() {
      var diff = target.getTime() - Date.now();
      if (diff <= 0) {
        el.textContent = expired || '00D : 00H : 00M : 00S';
        return false;
      }
      var d = Math.floor(diff / 86400000);
      var h = Math.floor((diff / 3600000) % 24);
      var m = Math.floor((diff / 60000) % 60);
      var s = Math.floor((diff / 1000) % 60);
      el.textContent = pad(d) + 'D : ' + pad(h) + 'H : ' + pad(m) + 'M : ' + pad(s) + 'S';
      return true;
    }
    if (tick()) {
      var id = this.every(function () { if (!tick()) clearInterval(id); }, 1000);
    }
  };

  /* ------------------------------------------------------------------ mobile menu */
  Instance.prototype.initMenu = function () {
    var toggle = $(this.root, '[data-ap-menu-toggle]');
    var menu = $(this.root, '[data-ap-mobile-menu]');
    if (!toggle || !menu) return;
    var self = this;
    this.setMenu = function (open) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      menu.classList.toggle('is-open', open);
    };
    this.on(toggle, 'click', function () {
      self.setMenu(toggle.getAttribute('aria-expanded') !== 'true');
    });
    $$(menu, 'a').forEach(function (a) {
      self.on(a, 'click', function () { self.setMenu(false); });
    });
    this.on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') { self.setMenu(false); toggle.focus(); }
    });
    this.on(document, 'click', function (e) {
      if (toggle.getAttribute('aria-expanded') === 'true' && !menu.contains(e.target) && !toggle.contains(e.target)) self.setMenu(false);
    });
  };

  /* ------------------------------------------------------------------ smooth in-page anchors */
  Instance.prototype.initAnchors = function () {
    var self = this;
    this.on(this.root, 'click', function (e) {
      var link = e.target.closest && e.target.closest('a[href^="#"]');
      if (!link || !self.root.contains(link)) return;
      var hash = link.getAttribute('href');
      if (hash.length < 2) { if (hash === '#') e.preventDefault(); return; }
      var target = null;
      try { target = document.querySelector(hash); } catch (err) { return; }
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'start' });
      if (history.pushState) history.pushState(null, '', hash);
    });
  };

  /* ------------------------------------------------------------------ hero chromatic word cycle */
  Instance.prototype.initWordCycle = function () {
    var el = $(this.root, '[data-ap-word]');
    if (!el) return;
    var words = (el.getAttribute('data-words') || '').split(',')
      .map(function (w) { return w.trim(); })
      .filter(Boolean);
    if (!words.length) return;
    var hold = (parseFloat(this.root.getAttribute('data-word-hold')) || 2.4) * 1000;
    var self = this;
    var index = 0;

    function render(word) {
      el.innerHTML = '';
      var frag = document.createDocumentFragment();
      word.split('').forEach(function (ch, i) {
        var span = document.createElement('span');
        span.className = 'ap-char';
        span.style.transitionDelay = (i * 40) + 'ms';
        span.textContent = ch;
        frag.appendChild(span);
      });
      el.appendChild(frag);
    }
    function cascadeIn(done) {
      var spans = el.querySelectorAll('.ap-char');
      void el.offsetWidth; // commit initial blurred state before transitioning
      requestAnimationFrame(function () {
        Array.prototype.forEach.call(spans, function (s) { s.classList.add('is-in'); });
      });
      self.later(done, spans.length * 40 + 600);
    }
    function cascadeOut(done) {
      var spans = el.querySelectorAll('.ap-char');
      Array.prototype.forEach.call(spans, function (s, i) {
        s.style.transitionDelay = (i * 18) + 'ms';
        s.classList.remove('is-in');
      });
      self.later(done, spans.length * 18 + 550);
    }
    function cycle() {
      if (!self.alive) return;
      if (REDUCED_MOTION) {
        el.textContent = words[index];
        index = (index + 1) % words.length;
        if (words.length > 1) self.later(cycle, hold + 800);
        return;
      }
      render(words[index]);
      cascadeIn(function () {
        if (words.length < 2) return;
        self.later(function () {
          cascadeOut(function () {
            index = (index + 1) % words.length;
            cycle();
          });
        }, hold);
      });
    }
    cycle();
  };

  /* ------------------------------------------------------------------ scroll reveals */
  Instance.prototype.initReveals = function () {
    var root = this.root;
    var els = $$(root, '[data-ap-reveal]');
    if (!els.length) return;
    var skip = this.designMode || REDUCED_MOTION || root.classList.contains('ap-no-reveal');
    if (skip) {
      els.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    if (hasGsap()) {
      window.gsap.registerPlugin(window.ScrollTrigger);
      var self = this;
      els.forEach(function (el) {
        self._tweenTargets.push(el);
        var tween = window.gsap.fromTo(el,
          { opacity: 0, y: 22 },
          {
            opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
            // clear the inline transform so CSS :hover lifts keep working after reveal
            clearProps: 'transform',
            scrollTrigger: { trigger: el, start: 'top 88%', once: true }
          });
        if (tween.scrollTrigger) self._triggers.push(tween.scrollTrigger);
      });
      return;
    }

    // Fallback: IntersectionObserver + Web Animations API
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);
        if (el.animate) {
          el.animate(
            [{ opacity: 0, transform: 'translateY(22px)' }, { opacity: 1, transform: 'translateY(0)' }],
            { duration: 900, easing: 'cubic-bezier(.22,1,.36,1)' }
          );
        }
        el.classList.add('is-revealed');
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    els.forEach(function (el) { io.observe(el); });
    this._observers.push(io);
  };

  /* ------------------------------------------------------------------ telemetry counters */
  Instance.prototype.initCounters = function () {
    var els = $$(this.root, '[data-ap-metric]');
    if (!els.length) return;
    var self = this;

    function format(el, v) {
      var decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
      return (el.getAttribute('data-prefix') || '') + v.toFixed(decimals) + (el.getAttribute('data-suffix') || '');
    }
    // Final values are server-rendered (no-JS & SEO safe). Width is reserved by
    // an invisible ghost copy, so counting up never shifts layout.
    if (REDUCED_MOTION || this.designMode) return;

    els.forEach(function (el) {
      var target = parseFloat(el.getAttribute('data-target'));
      if (isNaN(target)) return;
      el.textContent = format(el, 0);

      function run() {
        if (hasGsap()) {
          var obj = { val: 0 };
          self._tweenTargets.push(obj);
          window.gsap.to(obj, {
            val: target, duration: 1.6, ease: 'power2.out',
            onUpdate: function () { el.textContent = format(el, obj.val); }
          });
        } else {
          var start = performance.now();
          (function step(now) {
            if (!self.alive) return;
            var t = clamp((now - start) / 1600, 0, 1);
            el.textContent = format(el, target * easeOutQuad(t));
            if (t < 1) requestAnimationFrame(step);
          })(start);
        }
      }

      if (hasGsap()) {
        self._triggers.push(window.ScrollTrigger.create({ trigger: el, start: 'top 92%', once: true, onEnter: run }));
      } else if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) { io.disconnect(); run(); }
        }, { rootMargin: '0px 0px -8% 0px' });
        io.observe(el);
        self._observers.push(io);
      } else {
        el.textContent = format(el, target);
      }
    });
  };

  /* ------------------------------------------------------------------ FAQ drawers */
  Instance.prototype.initFaq = function () {
    var list = $(this.root, '[data-ap-faq]');
    if (!list) return;
    var multi = list.getAttribute('data-multi') === 'true';
    var items = $$(list, '[data-ap-faq-item]');
    function setOpen(item, open) {
      item.classList.toggle('is-open', open);
      var btn = $(item, '[data-ap-faq-trigger]');
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    this.openFaqItem = function (item) {
      if (!multi) items.forEach(function (other) { if (other !== item) setOpen(other, false); });
      setOpen(item, true);
    };
    var self = this;
    items.forEach(function (item) {
      var btn = $(item, '[data-ap-faq-trigger]');
      self.on(btn, 'click', function () {
        var open = !item.classList.contains('is-open');
        if (open) self.openFaqItem(item); else setOpen(item, false);
      });
    });
  };


  /* ------------------------------------------------------------------ scroll story */
  Instance.prototype.initStory = function () {
    var story = $(this.root, '[data-ap-story]');
    if (!story) return;
    var chapters = $$(story, '[data-ap-chapter]');
    var scenes = $$(story, '[data-ap-scene]');
    var dots = $$(story, '[data-ap-dot]');
    if (!chapters.length) return;
    var self = this;
    var active = -1;
    var ticking = false;
    var thresholds = scenes.map(function (sc) {
      return $$(sc, '[data-at]').map(function (el) { return { el: el, at: parseFloat(el.getAttribute('data-at')) || 0 }; });
    });

    function setActive(i) {
      if (i === active) return;
      active = i;
      chapters.forEach(function (c, k) { c.classList.toggle('is-active', k === i); });
      scenes.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
      dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
    }

    function update() {
      ticking = false;
      if (!self.alive) return;
      var vh = window.innerHeight;
      var focus = vh * 0.5;
      var sr = story.getBoundingClientRect();
      var span = sr.height + vh;
      story.style.setProperty('--p', clamp((vh - sr.top) / span, 0, 1).toFixed(4));

      var current = 0;
      chapters.forEach(function (c, k) {
        var r = c.getBoundingClientRect();
        if (r.top <= focus) current = k;
        // chapter progress: 0 when its top reaches the lower part of the screen, 1 when its bottom passes the focus line
        var cp = REDUCED_MOTION ? 1 : clamp((vh * 0.85 - r.top) / (r.height + vh * 0.35), 0, 1);
        var scene = scenes[k];
        if (scene) {
          scene.style.setProperty('--cp', cp.toFixed(4));
          thresholds[k].forEach(function (t) { t.el.classList.toggle('is-on', cp >= t.at); });
        }
      });
      setActive(current);
    }
    function onScroll() {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }
    this.on(window, 'scroll', onScroll, { passive: true });
    this.on(window, 'resize', onScroll, { passive: true });
    update();

    // Theme editor: selecting a chapter block scrolls its scene into view
    this.openStoryChapter = function (block) {
      var k = chapters.indexOf(block);
      if (k > -1) setActive(k);
    };
  };

  /* ------------------------------------------------------------------ executive intake */
  Instance.prototype.initIntake = function () {
    // Returning from a successful native Shopify post: bring the confirmation into view.
    if (/[?&]contact_posted=true/.test(window.location.search)) {
      var posted = $(this.root, '[data-ap-lead-success]:not([hidden])');
      if (posted) {
        this.later(function () { posted.scrollIntoView({ block: 'center', behavior: 'auto' }); }, 60);
      }
    }

    var wrap = $(this.root, '[data-ap-intake]');
    if (!wrap) return;
    var form = wrap.closest('form');
    if (!form) return;
    form.noValidate = true;

    var mode = wrap.getAttribute('data-mode') || 'shopify';
    var success = $(form, '[data-ap-lead-success]');
    var errorEl = $(wrap, '[data-ap-lead-error]');
    var nameEl = $(wrap, '[data-ap-lead-name]');
    var emailEl = $(wrap, '[data-ap-lead-email]');
    var msgEl = $(wrap, '[data-ap-lead-message]');
    var submitBtn = $(wrap, '[data-ap-lead-submit]');
    var servicesInput = $(wrap, '[data-ap-services-input]');
    var selected = [];
    var self = this;

    $$(wrap, '.ap-service-chip').forEach(function (chip) {
      self.on(chip, 'click', function () {
        var svc = chip.getAttribute('data-service');
        var idx = selected.indexOf(svc);
        if (idx > -1) selected.splice(idx, 1); else selected.push(svc);
        var active = idx === -1;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (servicesInput) servicesInput.value = selected.join(', ');
      });
    });

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || errorEl.getAttribute('data-default');
      errorEl.hidden = false;
    }
    function clearError() {
      if (errorEl) errorEl.hidden = true;
      [nameEl, emailEl].forEach(function (f) { if (f) f.removeAttribute('aria-invalid'); });
    }
    function showSuccess() {
      wrap.hidden = true;
      if (success) { success.hidden = false; success.focus({ preventScroll: true }); }
    }
    function validate() {
      var name = nameEl ? nameEl.value.trim() : '';
      var email = emailEl ? emailEl.value.trim() : '';
      var ok = true;
      if (!name) { ok = false; nameEl && nameEl.setAttribute('aria-invalid', 'true'); }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { ok = false; emailEl && emailEl.setAttribute('aria-invalid', 'true'); }
      return ok;
    }
    function setBusy(busy) {
      if (!submitBtn) return;
      submitBtn.disabled = busy;
      submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    this.on(form, 'submit', function (e) {
      clearError();
      if (!validate()) {
        e.preventDefault();
        showError();
        (nameEl && nameEl.getAttribute('aria-invalid') ? nameEl : emailEl).focus();
        return;
      }
      if (servicesInput) servicesInput.value = selected.join(', ');

      // Native Shopify contact form: let the browser post it (keeps Shopify's
      // spam protection / challenge flow intact). Success state is rendered
      // server-side via form.posted_successfully?.
      if (mode === 'shopify') { setBusy(true); return; }

      e.preventDefault();
      var payload = {
        name: nameEl ? nameEl.value.trim() : '',
        email: emailEl ? emailEl.value.trim() : '',
        message: msgEl ? msgEl.value.trim() : '',
        services: selected.slice(),
        recipient: wrap.getAttribute('data-recipient') || '',
        subject: wrap.getAttribute('data-subject') || '',
        page: window.location.href
      };

      if (mode === 'mailto') {
        var to = payload.recipient;
        var body = 'Name: ' + payload.name + '\nEmail: ' + payload.email +
          '\nServices: ' + (payload.services.join(', ') || '—') + '\n\n' + payload.message;
        window.location.href = 'mailto:' + encodeURIComponent(to) +
          '?subject=' + encodeURIComponent(payload.subject) + '&body=' + encodeURIComponent(body);
        showSuccess();
        return;
      }

      var endpoint = wrap.getAttribute('data-endpoint');
      if (!endpoint) { showError(errorEl && errorEl.getAttribute('data-submit-error')); return; }
      setBusy(true);
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        if (self.alive) showSuccess();
      }).catch(function () {
        if (self.alive) showError(errorEl && errorEl.getAttribute('data-submit-error'));
      }).then(function () {
        if (self.alive) setBusy(false);
      });
    });

  };

  /* ------------------------------------------------------------------ advisory terminal */
  Instance.prototype.initChat = function () {
    var fab = $(this.root, '[data-ap-chat-fab]');
    var modal = $(this.root, '[data-ap-chat-modal]');
    if (!fab || !modal) return;
    var closeBtn = $(modal, '[data-ap-chat-close]');
    var feed = $(modal, '[data-ap-chat-feed]');
    var form = $(modal, '[data-ap-chat-form]');
    var input = $(modal, '[data-ap-chat-input]');
    var sendBtn = $(modal, '[data-ap-send]');
    var pulse = $(modal, '[data-ap-inference]');
    var avatar = escapeHtml(feed.getAttribute('data-avatar') || '');
    var self = this;
    var open = false;
    var busy = false;

    var rules = $$(modal, 'template[data-ap-reply]').map(function (tpl) {
      return { groups: parseKeywords(tpl.getAttribute('data-keywords')), html: tpl.innerHTML };
    }).filter(function (r) { return r.groups.length; });
    var fallbackTpl = $(modal, 'template[data-ap-reply-fallback]');
    var fallback = fallbackTpl ? fallbackTpl.innerHTML : '';

    // "viban, banking, account+!crypto" → [[+viban],[+banking],[+account,-crypto]]
    function parseKeywords(str) {
      return String(str || '').split(',').map(function (alt) {
        return alt.split('+').map(function (term) {
          term = term.trim().toLowerCase();
          if (!term) return null;
          var neg = term.charAt(0) === '!';
          return { word: neg ? term.slice(1).trim() : term, neg: neg };
        }).filter(function (t) { return t && t.word; });
      }).filter(function (g) { return g.length; });
    }
    function match(text) { return matchRule(text) || fallback; }

    function setOpen(state) {
      open = state;
      modal.classList.toggle('is-open', open);
      fab.classList.toggle('is-open', open);
      fab.setAttribute('aria-expanded', open ? 'true' : 'false');
      modal.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) self.later(function () { input.focus({ preventScroll: true }); }, 260);
    }
    this.openChat = function () { setOpen(true); };

    this.on(fab, 'click', function () { setOpen(!open); });
    this.on(closeBtn, 'click', function () { setOpen(false); fab.focus(); });
    this.on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && open) { setOpen(false); fab.focus(); }
    });

    function scrollBottom() { feed.scrollTop = feed.scrollHeight; }
    function append(cls, html) {
      var wrap = document.createElement('div');
      wrap.className = 'ap-chat-msg ' + cls;
      wrap.innerHTML = html;
      feed.appendChild(wrap);
      scrollBottom();
      return wrap;
    }

    /* ---- live AI mode ---- */
    var mode = feed.getAttribute('data-mode') || 'demo';
    var endpoint = (feed.getAttribute('data-endpoint') || '').trim();
    var errorMsg = feed.getAttribute('data-error') || '';
    var live = mode === 'ai' && /^https?:\/\//i.test(endpoint);
    var history = [];          // [{role, content}] sent to the Worker
    var controller = null;
    this._abortChat = function () { if (controller) controller.abort(); };

    function matchRule(text) {
      var q = text.toLowerCase();
      for (var i = 0; i < rules.length; i++) {
        var hit = rules[i].groups.some(function (group) {
          return group.every(function (t) { return (q.indexOf(t.word) !== -1) !== t.neg; });
        });
        if (hit) return rules[i].html;
      }
      return null;
    }

    function startTurn(text) {
      busy = true;
      append('user', '<div class="ap-msg-bubble ap-user-bubble">' + escapeHtml(text) + '</div>');
      input.value = '';
      sendBtn.disabled = true;
      if (pulse) pulse.hidden = false;
      return append('bot', '<div class="ap-msg-avatar">' + avatar + '</div>' +
        '<div class="ap-msg-bubble ap-bot-bubble ap-thinking-bubble"><span class="ap-tdot"></span><span class="ap-tdot"></span><span class="ap-tdot"></span></div>');
    }
    function endTurn() {
      if (pulse) pulse.hidden = true;
      sendBtn.disabled = false;
      busy = false;
    }
    function botBubble(thinking, html) {
      var bubble = thinking.querySelector('.ap-msg-bubble');
      bubble.classList.remove('ap-thinking-bubble');
      bubble.innerHTML = html;
      scrollBottom();
      return bubble;
    }

    function askDemo(text) {
      var thinking = startTurn(text);
      self.later(function () {
        endTurn();
        botBubble(thinking, match(text));
      }, 900 + Math.random() * 500);
    }

    function askLive(text) {
      var thinking = startTurn(text);
      history.push({ role: 'user', content: text });
      controller = ('AbortController' in window) ? new AbortController() : null;
      var timer = self.later(function () { if (controller) controller.abort(); }, 45000);
      var acc = '';
      var bubble = null;
      var pending = false;

      function paint() {
        pending = false;
        if (!self.alive) return;
        if (!bubble) bubble = botBubble(thinking, '');
        bubble.innerHTML = renderMarkdown(acc);
        scrollBottom();
      }

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: history.slice(-12) }),
        signal: controller ? controller.signal : undefined
      }).then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        if (!res.body || !res.body.getReader) {
          return res.text().then(function (t) { acc = t; });
        }
        var reader = res.body.getReader();
        var decoder = new TextDecoder();
        return (function pump() {
          return reader.read().then(function (r) {
            if (r.done) { acc += decoder.decode(); return; }
            acc += decoder.decode(r.value, { stream: true });
            if (!pending) { pending = true; requestAnimationFrame(paint); }
            return pump();
          });
        })();
      }).then(function () {
        if (!self.alive) return;
        if (!acc.trim()) throw new Error('empty');
        paint();
        history.push({ role: 'assistant', content: acc });
      }).catch(function (err) {
        if (!self.alive) return;
        history.pop(); // don't keep an unanswered turn
        var local = matchRule(text);
        if (acc.trim()) { paint(); history.push({ role: 'user', content: text }, { role: 'assistant', content: acc }); }
        else botBubble(thinking, local || ('<p>' + escapeHtml(errorMsg) + '</p>'));
        if (window.console) console.warn('[Askpeply] advisory endpoint:', err && err.message);
      }).then(function () {
        clearTimeout(timer);
        controller = null;
        if (self.alive) endTurn();
      });
    }

    function ask(text) {
      text = String(text || '').trim().slice(0, 1500);
      if (!text || busy) return;
      if (live) askLive(text); else askDemo(text);
    }

    // Links inside bot answers: on-page anchors close the terminal first.
    this.on(feed, 'click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (a) setOpen(false);
    });

    this.on(form, 'submit', function (e) { e.preventDefault(); ask(input.value); });
    $$(modal, '[data-prompt]').forEach(function (chip) {
      self.on(chip, 'click', function () { ask(chip.getAttribute('data-prompt')); });
    });
  };

  /* ------------------------------------------------------------------ Three.js particle globe */
  Instance.prototype.initGlobe = function () {
    var root = this.root;
    var canvas = $(root, '[data-ap-canvas]');
    if (!canvas) return;
    if (typeof window.THREE === 'undefined') { canvas.style.display = 'none'; return; }

    try {
      this.globe = new Globe(this, canvas);
    } catch (err) {
      // No WebGL (old device / disabled GPU): the obsidian background remains.
      canvas.style.display = 'none';
      if (window.console) console.warn('[Askpeply] Globe disabled:', err && err.message);
    }
  };

  function Globe(inst, canvas) {
    var THREE = window.THREE;
    var root = inst.root;
    var density = clamp((parseFloat(canvas.getAttribute('data-density')) || 100) / 100, 0.3, 1.6);
    var STARDUST = canvas.getAttribute('data-mode') === 'stardust';
    var style = getComputedStyle(document.documentElement);

    var PALETTE = [
      hexToRgb01(style.getPropertyValue('--ap-violet'), [0.545, 0.361, 0.965]),
      hexToRgb01(style.getPropertyValue('--ap-fuchsia'), [0.851, 0.275, 0.937]),
      hexToRgb01(style.getPropertyValue('--ap-cyan'), [0.220, 0.741, 0.973]),
      [1, 1, 1]
    ];

    var W = window.innerWidth, H = window.innerHeight;
    var narrow = W < 640, tablet = W >= 640 && W < 1024;

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(54, W / H, 0.1, 1000);
    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(W, H, false);

    var baseYOffset = -3.2;
    function placeCamera() {
      var w = window.innerWidth;
      if (w < 640) { camera.position.z = 21; baseYOffset = -2.8; }
      else if (w < 1100) { camera.position.z = 19; baseYOffset = -3.2; }
      else { camera.position.z = 21.5; baseYOffset = -3.0; }
    }
    placeCamera();

    function continentValue(theta, phi) {
      var v = 0;
      v += Math.sin(theta * 2.1 + 1.3) * Math.cos(phi * 3.7 - 0.4);
      v += 0.5 * Math.sin(theta * 4.3 - 2.0) * Math.cos(phi * 2.2 + 1.1);
      v += 0.25 * Math.sin(theta * 7.7 + 0.6) * Math.sin(phi * 5.1 - 0.8);
      v += 0.15 * Math.cos(theta * 11.3 - 1.7) * Math.sin(phi * 8.4 + 2.3);
      return v / 1.9;
    }
    function gridStrength(theta, phi) {
      var step = Math.PI / 9;
      var latMod = Math.abs(((phi % step) + step) % step);
      var lonMod = Math.abs(((theta % step) + step) % step);
      var d = Math.min(Math.min(latMod, step - latMod), Math.min(lonMod, step - lonMod));
      var tol = step * 0.055;
      return d < tol ? (1 - d / tol) : 0;
    }
    function randomUnit() {
      var u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2, r = Math.sqrt(Math.max(0, 1 - u * u));
      return [r * Math.cos(th), r * Math.sin(th), u];
    }

    var N = Math.round((narrow ? 4200 : (tablet ? 6500 : 8800)) * density);
    var R = narrow ? 8.8 : (tablet ? 9.8 : 10.4);
    var GOLDEN = Math.PI * (3 - Math.sqrt(5));

    var sphere = new Float32Array(N * 3), start = new Float32Array(N * 3), disp = new Float32Array(N * 3);
    var positions = new Float32Array(N * 3), colors = new Float32Array(N * 3), phases = new Float32Array(N);

    for (var i = 0; i < N; i++) {
      var ix = i * 3;
      var yF = ((i * (2 / N)) - 1) + (1 / N);
      var rF = Math.sqrt(Math.max(0, 1 - yF * yF));
      var pF = i * GOLDEN;
      var xF = Math.cos(pF) * rF, zF = Math.sin(pF) * rF;
      sphere[ix] = xF * R; sphere[ix + 1] = yF * R; sphere[ix + 2] = zF * R;

      var dir = randomUnit(), aR = R * (2.2 + Math.random() * 1.5);
      start[ix] = dir[0] * aR; start[ix + 1] = dir[1] * aR; start[ix + 2] = dir[2] * aR - 4;

      disp[ix] = (Math.random() - 0.5) * 55;
      disp[ix + 1] = (Math.random() - 0.5) * 40;
      disp[ix + 2] = -Math.random() * 25 - 2;

      positions[ix] = start[ix]; positions[ix + 1] = start[ix + 1]; positions[ix + 2] = start[ix + 2];

      var theta = Math.atan2(zF, xF), phi = Math.asin(clamp(yF, -1, 1));
      var landT = clamp((continentValue(theta, phi) + 0.15) / 0.55, 0, 1);
      var grid = gridStrength(theta, phi);
      var ocean = PALETTE[Math.random() < 0.5 ? 0 : 1], land = PALETTE[Math.random() < 0.5 ? 2 : 3];
      var r = ocean[0] + (land[0] - ocean[0]) * landT;
      var g = ocean[1] + (land[1] - ocean[1]) * landT;
      var b = ocean[2] + (land[2] - ocean[2]) * landT;
      if (grid > 0) { r += (1 - r) * grid * 0.85; g += (1 - g) * grid * 0.85; b += (1 - b) * grid * 0.85; }
      var jit = 0.9 + Math.random() * 0.2;
      colors[ix] = Math.min(1, r * jit); colors[ix + 1] = Math.min(1, g * jit); colors[ix + 2] = Math.min(1, b * jit);
      phases[i] = Math.random() * Math.PI * 2;
    }

    var geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    var material = new THREE.PointsMaterial({
      vertexColors: true, size: narrow ? 0.052 : 0.062, sizeAttenuation: true,
      transparent: true, opacity: 0.92, depthWrite: false, blending: THREE.AdditiveBlending
    });
    scene.add(new THREE.Points(geometry, material));

    /* ---- interaction state ---- */
    var mouse = { x: 0, y: 0 }, lastMouse = { x: 0, y: 0 };
    var spinY = 0, spinX = 0, rotY = 0, rotX = 0;
    var scrollTarget = 0, scrollProgress = 0;
    var clock = new THREE.Clock();
    var ASSEMBLY_DELAY = 260, ASSEMBLY_DURATION = REDUCED_MOTION ? 1 : 2200;
    var BASE_SPIN = REDUCED_MOTION ? 0.00025 : 0.00075;
    var REPEL_RADIUS = 4.2, REPEL_STRENGTH = 1.35;
    var visible = true, pageVisible = !document.hidden;

    function setPointer(x, y) {
      mouse.x = (x / window.innerWidth) * 2 - 1;
      mouse.y = -(y / window.innerHeight) * 2 + 1;
    }
    inst.on(window, 'mousemove', function (e) { setPointer(e.clientX, e.clientY); }, { passive: true });
    inst.on(window, 'touchmove', function (e) {
      if (e.touches && e.touches.length) setPointer(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });

    // Scroll progress measured against this section, so the dispersal works whether
    // the section is the whole page (landing layout) or embedded in a theme page.
    function measureScroll() {
      var span = document.documentElement.scrollHeight - window.innerHeight;
      scrollTarget = span > 0 ? clamp(window.pageYOffset / span, 0, 1) : 0;
    }
    inst.on(window, 'scroll', measureScroll, { passive: true });
    measureScroll();
    scrollProgress = scrollTarget;

    var resizeRaf = 0;
    inst.on(window, 'resize', function () {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () {
        camera.aspect = window.innerWidth / window.innerHeight;
        placeCamera();
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight, false);
        measureScroll();
      });
    });

    // Pause rendering when the section is off-screen or the tab is hidden.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) loop();
      });
      io.observe(document.body);
      inst._observers.push(io);
    }
    inst.on(document, 'visibilitychange', function () {
      pageVisible = !document.hidden;
      if (pageVisible) { clock.getDelta(); loop(); }
    });

    function frame() {
      var elapsed = clock.getElapsedTime();
      var ms = elapsed * 1000;

      spinY = clamp(spinY + (mouse.x - lastMouse.x) * 0.02, -0.05, 0.05);
      spinX = clamp(spinX - (mouse.y - lastMouse.y) * 0.014, -0.05, 0.05);
      lastMouse.x = mouse.x; lastMouse.y = mouse.y;

      rotY += BASE_SPIN + spinY * 0.06 + mouse.x * 0.0003;
      rotX = clamp(rotX + spinX * 0.06, -0.2, 0.2);
      spinY *= 0.965; spinX *= 0.965;

      // Scrub-like smoothing (≈ ScrollTrigger scrub: 0.5)
      scrollProgress += (scrollTarget - scrollProgress) * 0.12;

      var eased = easeOutCubic(clamp((ms - ASSEMBLY_DELAY) / ASSEMBLY_DURATION, 0, 1));
      var dispersed = STARDUST ? 1 : Math.min(1, scrollProgress * 2.8);
      material.opacity = Math.max(0.38, 0.92 - dispersed * 0.35);

      var tanHalf = Math.tan((camera.fov * Math.PI / 180) / 2);
      var pos = geometry.attributes.position.array;
      var cosY = Math.cos(rotY), sinY = Math.sin(rotY), cosX = Math.cos(rotX), sinX = Math.sin(rotX);
      var driftMul = 0.35 + dispersed * 1.6;
      var repel = dispersed > 0.05;

      for (var i = 0; i < N; i++) {
        var ix = i * 3, ph = phases[i];
        var ax = start[ix] + (sphere[ix] - start[ix]) * eased;
        var ay = start[ix + 1] + (sphere[ix + 1] - start[ix + 1]) * eased;
        var az = start[ix + 2] + (sphere[ix + 2] - start[ix + 2]) * eased;

        var rx1 = ax * cosY - az * sinY;
        var rz1 = ax * sinY + az * cosY;
        var ry1 = ay * cosX - rz1 * sinX + baseYOffset;
        var rz2 = ay * sinX + rz1 * cosX;

        var bx = rx1 + (disp[ix] - rx1) * dispersed;
        var by = ry1 + (disp[ix + 1] - ry1) * dispersed;
        var bz = rz2 + (disp[ix + 2] - rz2) * dispersed;

        var drift = 0.08 + (i % 7) * 0.025;
        bx += Math.sin(elapsed * 0.22 + ph) * drift * driftMul;
        by += Math.cos(elapsed * 0.18 + ph * 1.3) * drift * driftMul + Math.sin(elapsed * 0.55 + ph) * 0.05;
        bz += Math.sin(elapsed * 0.15 + ph * 0.7) * drift * 0.8;

        if (repel) {
          var relZ = Math.max(0.5, camera.position.z - bz);
          var dx = bx - mouse.x * relZ * tanHalf * camera.aspect;
          var dy = by - mouse.y * relZ * tanHalf;
          var d = Math.sqrt(dx * dx + dy * dy);
          if (d > 0.0001 && d < REPEL_RADIUS) {
            var push = (1 - d / REPEL_RADIUS) * REPEL_STRENGTH * dispersed;
            bx += (dx / d) * push;
            by += (dy / d) * push;
          }
        }
        pos[ix] = bx; pos[ix + 1] = by; pos[ix + 2] = bz;
      }
      geometry.attributes.position.needsUpdate = true;
      renderer.render(scene, camera);
    }

    var running = false;
    function loop() {
      if (running || !inst.alive) return;
      running = true;
      (function tick() {
        if (!inst.alive || !visible || !pageVisible) { running = false; return; }
        frame();
        inst._raf = requestAnimationFrame(tick);
      })();
    }
    loop();

    this.dispose = function () {
      cancelAnimationFrame(resizeRaf);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (renderer.forceContextLoss) renderer.forceContextLoss();
    };
  }

  /* ================================================================== bootstrapping */
  function boot(scope) {
    var roots = (scope || document).querySelectorAll ? (scope || document).querySelectorAll('[data-ap-root]') : [];
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__igInstance) return;
      var inst = new Instance(root);
      root.__igInstance = inst;
      instances.push(inst);
      try { inst.init(); } catch (err) {
        root.classList.add('ap-booted');
        if (window.console) console.error('[Askpeply] init failed', err);
      }
    });
  }
  function teardown(scope) {
    instances = instances.filter(function (inst) {
      if (scope.contains(inst.root)) {
        inst.destroy();
        inst.root.__igInstance = null;
        return false;
      }
      return true;
    });
  }
  function instanceFor(el) {
    for (var i = 0; i < instances.length; i++) if (instances[i].root.contains(el)) return instances[i];
    return null;
  }

  // Theme editor integration
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var inst = instanceFor(e.target);
    if (!inst) return;
    var block = e.target;
    if (block.matches('[data-ap-faq-item]') && inst.openFaqItem) inst.openFaqItem(block);
    if (block.matches('[data-ap-chapter]') && inst.openStoryChapter) inst.openStoryChapter(block);
    if (block.matches('template, [data-ap-reply]') && inst.openChat) inst.openChat();
    block.classList.add('is-revealed');
  });

  window.Askpeply = { boot: boot, teardown: teardown, instances: function () { return instances.slice(); } };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
})();
